import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton, Collapse, Box, Typography } from '@mui/material';
import { Close, ExpandMore, ExpandLess, Error, Warning, FolderOff, HelpOutline } from '@mui/icons-material';

const extractIMEI = (msg) => {
    const match = msg.match(/IMEI[:\s]*(\d+)/i);
    return match ? match[1] : null;
};

const groupErrors = (errors) => {
    const notFoundIMEIs = errors.notFound?.map(msg => extractIMEI(msg)).filter(Boolean) || [];

    const configGroups = {};
    (errors.configIssues || []).forEach(msg => {
        let type = '';
        if (msg.includes('Application version is missing')) type = 'Application version is missing, restart the device';
        else if (msg.includes('Flash version is missing')) type = 'Flash version is missing, restart the device';
        else if (msg.includes('MCU Type is missing')) type = 'MCU Type is missing';
        else type = 'Configuration issue';

        if (!configGroups[type]) configGroups[type] = [];
        const imei = extractIMEI(msg);
        if (imei) configGroups[type].push(imei);
    });

    const fileGroups = {};
    (errors.fileMissing || []).forEach(msg => {
        let type = '';
        if (msg.includes('Firmware file missing')) {
            const match = msg.match(/Firmware file missing for (.+?) for IMEI/);
            type = match ? `Firmware file missing for ${match[1]}` : 'Firmware file missing';
        } else if (msg.includes('Bootloader file missing')) {
            const match = msg.match(/Bootloader file missing for (.+?) for IMEI/);
            type = match ? `Bootloader file missing for ${match[1]}` : 'Bootloader file missing';
        } else {
            type = 'File missing';
        }

        if (!fileGroups[type]) fileGroups[type] = [];
        const imei = extractIMEI(msg);
        if (imei) fileGroups[type].push(imei);
    });

    return { notFoundIMEIs, configGroups, fileGroups, otherErrors: errors.otherErrors || [] };
};

const ErrorSection = ({ title, icon, color, count, children }) => {
    const [expanded, setExpanded] = useState(false);

    if (count === 0) return null;

    return (
        <Box sx={{ border: 1, borderColor: color, borderRadius: 1, mb: 1.5, bgcolor: `${color}15` }}>
            <Box
                onClick={() => setExpanded(!expanded)}
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1.5, cursor: 'pointer' }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {icon}
                    <Typography fontWeight="medium">{title}</Typography>
                    <Typography variant="body2" color="text.secondary">({count})</Typography>
                </Box>
                {expanded ? <ExpandLess /> : <ExpandMore />}
            </Box>
            <Collapse in={expanded}>
                <Box sx={{ px: 1.5, pb: 1.5 }}>{children}</Box>
            </Collapse>
        </Box>
    );
};

const OTAErrorModal = ({ open, onClose, errors }) => {
    if (!errors) return null;

    const grouped = groupErrors(errors);
    const configCount = Object.values(grouped.configGroups).flat().length;
    const fileCount = Object.values(grouped.fileGroups).flat().length;
    const totalErrors = grouped.notFoundIMEIs.length + configCount + fileCount + grouped.otherErrors.length;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Error color="error" />
                    <Box>
                        <Typography variant="h6">OTA Configuration Errors</Typography>
                        <Typography variant="body2" color="text.secondary">
                            {totalErrors} device{totalErrors !== 1 ? 's' : ''} affected
                        </Typography>
                    </Box>
                </Box>
                <IconButton onClick={onClose}><Close /></IconButton>
            </DialogTitle>

            <DialogContent dividers>
                <ErrorSection
                    title="Devices Not Found"
                    icon={<Error color="error" />}
                    color="error.main"
                    count={grouped.notFoundIMEIs.length}
                >
                    <Box sx={{ bgcolor: 'white', p: 1.5, borderRadius: 1, border: 1, borderColor: 'grey.200' }}>
                        <Typography variant="body2">
                            <strong>IMEIs: </strong>{grouped.notFoundIMEIs.join(', ')}
                        </Typography>
                    </Box>
                </ErrorSection>

                <ErrorSection
                    title="Configuration Issues"
                    icon={<Warning color="warning" />}
                    color="warning.main"
                    count={configCount}
                >
                    {Object.entries(grouped.configGroups).map(([type, imeis], idx) => (
                        <Box key={idx} sx={{ bgcolor: 'white', p: 1.5, borderRadius: 1, border: 1, borderColor: 'grey.200', mb: 1 }}>
                            <Typography variant="body2" fontWeight="medium">{type}</Typography>
                            <Typography variant="body2" color="text.secondary">IMEIs: {imeis.join(', ')}</Typography>
                        </Box>
                    ))}
                </ErrorSection>

                <ErrorSection
                    title="Missing Files"
                    icon={<FolderOff sx={{ color: 'orange' }} />}
                    color="warning.dark"
                    count={fileCount}
                >
                    {Object.entries(grouped.fileGroups).map(([type, imeis], idx) => (
                        <Box key={idx} sx={{ bgcolor: 'white', p: 1.5, borderRadius: 1, border: 1, borderColor: 'grey.200', mb: 1 }}>
                            <Typography variant="body2" fontWeight="medium">{type}</Typography>
                            <Typography variant="body2" color="text.secondary">IMEIs: {imeis.join(', ')}</Typography>
                        </Box>
                    ))}
                </ErrorSection>

                <ErrorSection
                    title="Other Errors"
                    icon={<HelpOutline color="action" />}
                    color="grey.500"
                    count={grouped.otherErrors.length}
                >
                    {grouped.otherErrors.map((error, idx) => (
                        <Box key={idx} sx={{ bgcolor: 'white', p: 1.5, borderRadius: 1, border: 1, borderColor: 'grey.200', mb: 1 }}>
                            <Typography variant="body2">{error}</Typography>
                        </Box>
                    ))}
                </ErrorSection>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} variant="contained" fullWidth>Close</Button>
            </DialogActions>
        </Dialog>
    );
};

export default OTAErrorModal;